import { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "../components/Common";
import { toast } from "sonner";
import { validateUpload } from "../lib/upload";
import { FloppyDisk, UploadSimple } from "@phosphor-icons/react";

const EXPENSE_CATS = ["Yakıt Gideri", "Bakım-Onarım", "Kasko & Sigorta", "Kredi Taksidi", "Personel Maaşı", "Vergi & Harç", "Lastik", "Diğer Gider"];

export default function Settings() {
  const [form, setForm] = useState({ company_name: "", address: "", phone: "", email: "", tax_office: "", tax_number: "", logo_path: null, receipt_required_categories: [] });
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setForm({ ...form, ...data });
      if (data.logo_path) loadLogo(data.logo_path);
    });
    // eslint-disable-next-line
  }, []);

  const loadLogo = async (path) => {
    try {
      const { data } = await api.get(`/files/${path}`, { responseType: "blob" });
      setLogoUrl(URL.createObjectURL(data));
    } catch (err) { console.error("Logo yüklenemedi:", err); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!validateUpload(file, "image")) { e.target.value = ""; return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload?kind=image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo_path: data.path }));
      loadLogo(data.path);
      toast.success("Logo yüklendi");
    } catch {
      toast.error("Logo yüklenemedi");
    } finally {
      setUploading(false);
    }
  };

  const toggleCat = (c) => {
    setForm((f) => {
      const list = f.receipt_required_categories || [];
      return { ...f, receipt_required_categories: list.includes(c) ? list.filter((x) => x !== c) : [...list, c] };
    });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/settings", form);
      toast.success("Ayarlar kaydedildi");
    } catch {
      toast.error("Kaydedilemedi");
    }
  };

  const field = (key, label, placeholder = "") => (
    <div>
      <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-zinc-500 mb-1.5">{label}</label>
      <input value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} data-testid={`settings-${key}`} placeholder={placeholder} className="inp" />
    </div>
  );

  return (
    <div className="p-8 max-w-[900px]">
      <PageHeader title="Şirket Ayarları" subtitle="Sözleşme belgelerinde ve raporlarda kullanılacak şirket bilgileri" />

      <form onSubmit={save} className="bg-white border border-zinc-200 rounded-sm p-8 animate-fade-up space-y-6" data-testid="settings-form">
        <div className="flex items-center gap-6">
          <div className="w-24 h-24 border border-zinc-200 rounded-sm flex items-center justify-center bg-zinc-50 overflow-hidden shrink-0">
            {logoUrl ? <img src={logoUrl} alt="logo" className="w-full h-full object-contain" data-testid="settings-logo-preview" /> : <span className="text-xs text-zinc-400">Logo yok</span>}
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-zinc-500 mb-2">Şirket Logosu</label>
            <label className="inline-flex items-center gap-2 border border-zinc-300 rounded-sm px-4 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer" data-testid="settings-logo-upload">
              <UploadSimple size={16} weight="bold" />
              {uploading ? "Yükleniyor..." : "Logo Yükle"}
              <input type="file" accept="image/*" onChange={uploadLogo} className="hidden" />
            </label>
            <p className="text-xs text-zinc-400 mt-2">PNG veya JPG. Sözleşme PDF'inin üst kısmında görünür.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {field("company_name", "Şirket Adı", "Örnek Filo Kiralama A.Ş.")}
          {field("phone", "Telefon", "0212 000 00 00")}
          {field("email", "E-posta", "info@sirket.com")}
          {field("tax_office", "Vergi Dairesi", "Kadıköy")}
          {field("tax_number", "Vergi No", "1234567890")}
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-zinc-500 mb-1.5">Adres</label>
          <textarea value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="settings-address" rows={2} className="inp" placeholder="Mahalle, Cadde, No, İlçe/İl" />
        </div>

        <div>
          <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-zinc-500 mb-2">Fiş Zorunlu Gider Kategorileri</label>
          <p className="text-xs text-zinc-400 mb-3">Seçili kategorilerde gider eklerken fatura/fiş yüklemek zorunlu olur.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {EXPENSE_CATS.map((c) => {
              const on = (form.receipt_required_categories || []).includes(c);
              return (
                <button type="button" key={c} onClick={() => toggleCat(c)} data-testid={`req-cat-${c}`} className={`text-left text-sm px-3 py-2 rounded-sm border transition-colors ${on ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50"}`}>
                  {on ? "✓ " : ""}{c}
                </button>
              );
            })}
          </div>
        </div>

        <button type="submit" data-testid="settings-save" className="inline-flex items-center gap-2 bg-zinc-950 text-white rounded-sm px-6 py-2.5 text-sm font-semibold hover:bg-zinc-800 transition-colors">
          <FloppyDisk size={16} weight="bold" /> Kaydet
        </button>
      </form>
    </div>
  );
}
